import { useEffect, useRef, memo } from 'react';

const WidgetController = memo(({ children, showWidget = false }) => {
  const ref = useRef(null);
  const showWidgetRef = useRef(showWidget);
  const lastStateRef = useRef(null);
  const rafIdRef = useRef(null);
  const isCheckingRef = useRef(false);
  const widgetReadyRef = useRef(false);
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 50; // ~5 seconds at 100ms intervals
  const RETRY_INTERVAL = 100;

  // Keep showWidget in sync
  useEffect(() => {
    showWidgetRef.current = showWidget;
  }, [showWidget]);

  // Wait for widget to be available
  useEffect(() => {
    let retryTimer;

    const waitForWidget = () => {
      if (window.AngelAI && typeof window.AngelAI.show === 'function') {
        widgetReadyRef.current = true;
        retryCountRef.current = 0;
        checkVisibility(); // Initial check once widget is ready
        return;
      }

      retryCountRef.current++;
      if (retryCountRef.current >= MAX_RETRIES) {
        return;
      }

      retryTimer = setTimeout(waitForWidget, RETRY_INTERVAL);
    };

    waitForWidget();

    return () => {
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, []);

  const checkVisibility = () => {
    if (!ref.current || isCheckingRef.current || !widgetReadyRef.current) return;

    isCheckingRef.current = true;

    const rect = ref.current.getBoundingClientRect();
    const isVisible = rect.top < window.innerHeight && rect.bottom > 0;

    const shouldShow = isVisible === showWidgetRef.current;

    // Skip if state hasn't changed
    if (lastStateRef.current === shouldShow) {
      isCheckingRef.current = false;
      return;
    }

    lastStateRef.current = shouldShow;

    if (shouldShow) {
      window.AngelAI?.show?.();
    } else {
      window.AngelAI?.hide?.();
    }

    isCheckingRef.current = false;
  };

  useEffect(() => {
    if (!ref.current) return;

    const handleScroll = () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
      rafIdRef.current = requestAnimationFrame(checkVisibility);
    };

    // Listen to scroll with passive for better mobile performance
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('scrollend', checkVisibility, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scrollend', checkVisibility);
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, []);

  return (
    <div ref={ref} style={{ minHeight: '1px' }}>
      {children}
    </div>
  );
});

WidgetController.displayName = 'WidgetController';
export default WidgetController;